import {
  type LinkedExternalInstitution,
  LinkedInstitutionStatus,
  useCompleteUpdatePlaidExternalAccount,
  useCreatePlaidMultiLinkToken,
  useInitiateUpdatePlaidExternalAccount,
  useUnlinkExternalAccountsByInstitution,
  useCustomerDetails,
  type PlaidLinkType,
  type InitiateRelinkExternalAccountOutput,
  type InitiateUpdatePlaidExternalAccountResult,
  useCompleteRelinkExternalAccount,
  type CompleteRelinkExternalAccountResult,
  useInitiateRelinkExternalInstitution,
} from '@egw/api'
import { useModalState } from '@egw/components'
import { sendEvent } from '@egw/events'
import { useTranslation } from '@egw/i18n'
import { Logger } from '@egw/logger'
import { createContext } from '@egw/utils'
import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  PlaidErrorType,
  PlaidMultiLinkState,
  PlaidSuccessType,
  UpdateActionType,
  type PlaidLinkOnSuccessMetadata,
  type PlaidMultiLinkHookReturn,
} from '../../../types/plaid-multi-link'

const log = new Logger('Plaid Multi Link Modal')

/**
 * Custom hook for Plaid Multi Link functionality
 */
export const usePlaidMultiLink = (): PlaidMultiLinkHookReturn => {
  const { t } = useTranslation('features-external-accounts')
  // Modal state
  const { isOpen, openModal: openModalHandler, closeModal: closeModalHandler } = useModalState()

  // Institution Being Updated
  const [institutionId, setInstitutionId] = useState<string | undefined>(undefined)

  const [modalTitle, setModalTitle] = useState<string>('')

  const INITIAL_STATE = useMemo(
    () =>
      institutionId !== undefined
        ? PlaidMultiLinkState.INSTITUTION_MANAGEMENT_SELECTION // If institutionId is defined, show institution management selection
        : PlaidMultiLinkState.ACCOUNT_LINK_TYPE_SELECTION, // If institutionId is not defined, show account link type selection (Banking |Investments)
    [institutionId],
  )

  // Plaid flow state
  const [currentState, setCurrentState] = useState<PlaidMultiLinkState>(INITIAL_STATE)
  const [selectedLinkType, setSelectedLinkType] = useState<PlaidLinkType | undefined>(undefined)
  const [plaidLinkToken, setPlaidLinkToken] = useState<string | undefined>(undefined)

  // Error and success state
  const [currentErrorType, setCurrentErrorType] = useState<PlaidErrorType | undefined>(undefined)
  const [currentSuccessType, setCurrentSuccessType] = useState<PlaidSuccessType | undefined>(undefined)

  // Reset plaid flow state when modal opens or alias changes
  useEffect(() => {
    if (isOpen) {
      setCurrentState(INITIAL_STATE)
      setSelectedLinkType(undefined)
      setPlaidLinkToken(undefined)
      setCurrentErrorType(undefined)
      setCurrentSuccessType(undefined)
    }
  }, [isOpen, INITIAL_STATE])

  const { data: customerDetails, refetch: refetchExternalAccounts } = useCustomerDetails({ enabled: false })

  const isChaseBank: boolean = useMemo(() => {
    if (!institutionId) return false
    return institutionId === 'ins_56'
  }, [institutionId])

  const showRelink = useMemo(() => {
    if (isChaseBank) return false
    if (!customerDetails?.customer?.externalAccounts?.institutions) return false

    const status = customerDetails.customer.externalAccounts.institutions.find(
      (institution: LinkedExternalInstitution) => institution.institutionId === institutionId,
    )?.linkedInstitutionStatus

    return status === LinkedInstitutionStatus.EXPIRED || status === LinkedInstitutionStatus.EXPIRING_SOON
  }, [customerDetails, institutionId, isChaseBank])

  // Hook for creating Plaid Multi Link token for linking institution
  const { mutate: createPlaidMultiLinkToken, isLoading: isCreatingPlaidMultiLinkToken } = useCreatePlaidMultiLinkToken({
    onSuccess: (res) => {
      if (res.__typename === 'PlaidLinkToken') {
        setPlaidLinkToken(res.linkToken)
        setCurrentState(PlaidMultiLinkState.INITIAL_INSTITUTION_LINKING_IN_PROGRESS)
        sendEvent('eg_plaidMultiLink_tokenCreated', {
          eventSource: 'egWealth',
          category: 'plaid-multi-link',
          subCategory: 'token-creation',
          linkType: selectedLinkType,
        })
      } else {
        setCurrentErrorType(PlaidErrorType.INITIAL_INSTITUTION_LINK_TOKEN_CREATION)
        setCurrentState(PlaidMultiLinkState.ERROR)
        sendEvent('eg_plaidMultiLink_tokenError', {
          eventSource: 'egWealth',
          category: 'plaid-multi-link',
          subCategory: 'token-creation',
          linkType: selectedLinkType,
          error: res,
        })
      }
    },
    onError: (error) => {
      setCurrentErrorType(PlaidErrorType.INITIAL_INSTITUTION_LINK_TOKEN_CREATION)
      setCurrentState(PlaidMultiLinkState.ERROR)
      sendEvent('eg_plaidMultiLink_tokenError', {
        eventSource: 'egWealth',
        category: 'plaid-multi-link',
        subCategory: 'token-creation',
        linkType: selectedLinkType,
        error,
      })
    },
  })

  // Hook for unlinking external institution
  const { mutate: unlinkPlaidExternalInstitution, isLoading: isUnlinkingPlaidExternalInstitution } =
    useUnlinkExternalAccountsByInstitution({
      onSuccess: (res) => {
        if (res.__typename === 'UnlinkExternalAccountsByInstitutionOutput' && res.isSuccessful) {
          setCurrentSuccessType(PlaidSuccessType.REMOVE_INSTITUTION)
          setCurrentState(PlaidMultiLinkState.SUCCESS)
          sendEvent('eg_plaidMultiLink_accountUnlinked', {
            eventSource: 'egWealth',
            category: 'plaid-multi-link',
            subCategory: 'account-removal',
            institutionId,
          })
          void refetchExternalAccounts()
        } else {
          setCurrentErrorType(PlaidErrorType.INSTITUTION_REMOVAL_ERROR)
          setCurrentState(PlaidMultiLinkState.ERROR)
          sendEvent('eg_plaidMultiLink_unlinkError', {
            eventSource: 'egWealth',
            category: 'plaid-multi-link',
            subCategory: 'account-removal',
            institutionId,
            error: res,
          })
        }
      },
      onError: (error) => {
        setCurrentState(PlaidMultiLinkState.ERROR)
        setCurrentErrorType(PlaidErrorType.INSTITUTION_REMOVAL_ERROR)
        sendEvent('eg_plaidMultiLink_unlinkError', {
          eventSource: 'egWealth',
          category: 'plaid-multi-link',
          subCategory: 'account-removal',
          institutionId,
          error,
        })
      },
    })

  // Hook for initiating relink operation to get a new token
  const { mutate: initiateRelinkPlaidExternalInstitution, isLoading: isInitiatingRelinkPlaidExternalInstitution } =
    useInitiateRelinkExternalInstitution({
      onSuccess: (res: InitiateRelinkExternalAccountOutput) => {
        if (res.__typename === 'PlaidLinkToken') {
          setPlaidLinkToken(res.linkToken)
          setCurrentState(PlaidMultiLinkState.RELINKING_INSTITUTION_IN_PROGRESS)
          sendEvent('eg_plaidMultiLink_relinkTokenCreated', {
            eventSource: 'egWealth',
            category: 'plaid-multi-link',
            subCategory: 'relink-token-creation',
            institutionId,
          })
        } else {
          setCurrentState(PlaidMultiLinkState.ERROR)
          setCurrentErrorType(PlaidErrorType.RELINK_INSTITUTION_TOKEN_CREATION)
          sendEvent('eg_plaidMultiLink_relinkTokenError', {
            eventSource: 'egWealth',
            category: 'plaid-multi-link',
            subCategory: 'relink-token-creation',
            institutionId,
            error: res,
          })
        }
      },
      onError: (error) => {
        setCurrentState(PlaidMultiLinkState.ERROR)
        setCurrentErrorType(PlaidErrorType.RELINK_INSTITUTION_TOKEN_CREATION)
        sendEvent('eg_plaidMultiLink_relinkTokenError', {
          eventSource: 'egWealth',
          category: 'plaid-multi-link',
          subCategory: 'relink-token-creation',
          institutionId,
          error,
        })
      },
    })

  const { mutate: completeRelinkPlaidExternalInstitution, isLoading: isCompletingRelinkPlaidExternalAccount } =
    useCompleteRelinkExternalAccount({
      onSuccess: (res: CompleteRelinkExternalAccountResult) => {
        if (res.__typename === 'CompleteRelinkExternalAccountOutput') {
          setCurrentSuccessType(PlaidSuccessType.RELINK_INSTITUTION)
          setCurrentState(PlaidMultiLinkState.SUCCESS)
          void refetchExternalAccounts()
          sendEvent('eg_plaidMultiLink_updateComplete', {
            eventSource: 'egWealth',
            category: 'plaid-multi-link',
            subCategory: 'update-complete',
            institutionId,
          })
        } else {
          setCurrentState(PlaidMultiLinkState.ERROR)
          setCurrentErrorType(PlaidErrorType.RELINK_INSTITUTION_ERROR)
          sendEvent('eg_plaidMultiLink_updateCompleteError', {
            eventSource: 'egWealth',
            category: 'plaid-multi-link',
            subCategory: 'update-complete',
            institutionId,
            error: res,
          })
        }
      },
      onError: (error) => {
        setCurrentState(PlaidMultiLinkState.ERROR)
        setCurrentErrorType(PlaidErrorType.RELINK_INSTITUTION_ERROR)
        sendEvent('eg_plaidMultiLink_updateCompleteError', {
          eventSource: 'egWealth',
          category: 'plaid-multi-link',
          subCategory: 'update-complete',
          institutionId,
          error,
        })
      },
    })

  // Hook for initiating relink operation to get a new token
  const { mutate: initiateUpdatePlaidExternalInstitution, isLoading: isUpdatingPlaidExternalInstitution } =
    useInitiateUpdatePlaidExternalAccount({
      onSuccess: (res: InitiateUpdatePlaidExternalAccountResult) => {
        if (res.__typename === 'PlaidLinkToken') {
          setPlaidLinkToken(res.linkToken)
          setCurrentState(PlaidMultiLinkState.UPDATING_INSTITUTION_IN_PROGRESS)
          sendEvent('eg_plaidMultiLink_updateTokenCreated', {
            eventSource: 'egWealth',
            category: 'plaid-multi-link',
            subCategory: 'update-token-creation',
            institutionId,
          })
        } else {
          setCurrentState(PlaidMultiLinkState.ERROR)
          setCurrentErrorType(PlaidErrorType.UPDATING_INSTITUTION_TOKEN_CREATION)
          sendEvent('eg_plaidMultiLink_updateTokenError', {
            eventSource: 'egWealth',
            category: 'plaid-multi-link',
            subCategory: 'update-token-creation',
            institutionId,
            error: res,
          })
        }
      },
      onError: (error) => {
        setCurrentState(PlaidMultiLinkState.ERROR)
        setCurrentErrorType(PlaidErrorType.UPDATING_INSTITUTION_TOKEN_CREATION)
        sendEvent('eg_plaidMultiLink_updateTokenError', {
          eventSource: 'egWealth',
          category: 'plaid-multi-link',
          subCategory: 'update-token-creation',
          institutionId,
          error,
        })
      },
    })

  // Hook for completing relink operation to let BE know that the relink is complete since no webhook is available
  const { mutate: completeUpdatePlaidExternalInstitution, isLoading: isCompletingUpdatePlaidExternalAccount } =
    useCompleteUpdatePlaidExternalAccount({
      onSuccess: (res) => {
        if (res.__typename === 'CompleteUpdatePlaidExternalAccountSuccess') {
          setCurrentSuccessType(PlaidSuccessType.UPDATING_INSTITUTION)
          setCurrentState(PlaidMultiLinkState.SUCCESS)
          void refetchExternalAccounts()
          sendEvent('eg_plaidMultiLink_updateComplete', {
            eventSource: 'egWealth',
            category: 'plaid-multi-link',
            subCategory: 'update-complete',
            institutionId,
          })
        } else {
          setCurrentState(PlaidMultiLinkState.ERROR)
          setCurrentErrorType(PlaidErrorType.UPDATING_INSTITUTION_ERROR)
          sendEvent('eg_plaidMultiLink_updateCompleteError', {
            eventSource: 'egWealth',
            category: 'plaid-multi-link',
            subCategory: 'update-complete',
            institutionId,
            error: res,
          })
        }
      },
      onError: (error) => {
        setCurrentState(PlaidMultiLinkState.ERROR)
        setCurrentErrorType(PlaidErrorType.UPDATING_INSTITUTION_ERROR)
        sendEvent('eg_plaidMultiLink_updateCompleteError', {
          eventSource: 'egWealth',
          category: 'plaid-multi-link',
          subCategory: 'update-complete',
          institutionId,
          error,
        })
      },
    })

  // Handle button click with proper typing
  const handleAccountTypeSelection = useCallback(
    (linkType: PlaidLinkType): void => {
      setSelectedLinkType(linkType)
      sendEvent('eg_plaidMultiLink_accountTypeSelected', {
        eventSource: 'egWealth',
        category: 'plaid-multi-link',
        subCategory: 'account-selection',
        linkType,
      })
      void createPlaidMultiLinkToken({ linkType })
    },
    [createPlaidMultiLinkToken],
  )

  // Modal control methods
  const openModal = useCallback(
    (options?: { institutionId?: string; modalTitle?: string }): void => {
      log.info('Opening modal', { options })
      const { institutionId: newInstitutionId, modalTitle: newModalTitle } = options ?? {}
      setInstitutionId(newInstitutionId)
      setModalTitle(newModalTitle ?? t('plaid_multi_link.title'))
      openModalHandler()
    },
    [openModalHandler, t],
  )

  const closeModal = useCallback((): void => {
    log.info('Closing modal and resetting state')
    closeModalHandler()

    // Send event
    sendEvent('eg_plaidMultiLink_cancel', {
      eventSource: 'egWealth',
      category: 'plaid-multi-link',
      subCategory: 'modal-close',
      linkType: selectedLinkType,
      currentState,
    })

    // Reset state
    setCurrentState(INITIAL_STATE)
    setSelectedLinkType(undefined)
    setPlaidLinkToken(undefined)
    setCurrentErrorType(undefined)
    setCurrentSuccessType(undefined)
  }, [selectedLinkType, currentState, INITIAL_STATE, closeModalHandler])

  const handleTryAgain = useCallback((): void => {
    log.info('handleTryAgain invoked')
    sendEvent('eg_plaidMultiLink_tryAgain', {
      eventSource: 'egWealth',
      category: 'plaid-multi-link',
      subCategory: 'retry',
      linkType: selectedLinkType,
      previousState: currentState,
    })

    switch (currentErrorType) {
      case PlaidErrorType.INITIAL_INSTITUTION_LINK_ERROR:
      case PlaidErrorType.INITIAL_INSTITUTION_LINK_TOKEN_CREATION:
        log.info('Retrying Initial Institution Link Token Creation')
        setCurrentErrorType(undefined)
        setPlaidLinkToken(undefined)
        selectedLinkType !== undefined && void createPlaidMultiLinkToken({ linkType: selectedLinkType })
        break

      case PlaidErrorType.INSTITUTION_REMOVAL_ERROR:
        // Retry unlinking operation
        log.info('Retrying unlinking operation for INSTITUTION_REMOVAL_ERROR')
        setCurrentErrorType(undefined)
        institutionId !== undefined && void unlinkPlaidExternalInstitution({ institutionId })
        break

      case PlaidErrorType.RELINK_INSTITUTION_TOKEN_CREATION:
      case PlaidErrorType.RELINK_INSTITUTION_ERROR:
        log.info('Retrying Relink Institution Token Creation')
        setCurrentErrorType(undefined)
        setPlaidLinkToken(undefined)
        institutionId !== undefined && void initiateRelinkPlaidExternalInstitution({ institutionId })
        break

      case PlaidErrorType.UPDATING_INSTITUTION_TOKEN_CREATION:
      case PlaidErrorType.UPDATING_INSTITUTION_ERROR:
        log.info('Retrying Relink Institution Token Creation')
        setCurrentErrorType(undefined)
        setPlaidLinkToken(undefined)
        institutionId !== undefined && void initiateUpdatePlaidExternalInstitution({ institutionId })
        break

      default:
        log.info('No error type. Resetting state and closing modal')
        setCurrentErrorType(undefined)
        setCurrentSuccessType(undefined)
        setPlaidLinkToken(undefined)
        setCurrentState(INITIAL_STATE)
        break
    }
  }, [
    currentState,
    selectedLinkType,
    currentErrorType,
    createPlaidMultiLinkToken,
    institutionId,
    unlinkPlaidExternalInstitution,
    initiateRelinkPlaidExternalInstitution,
    initiateUpdatePlaidExternalInstitution,
    INITIAL_STATE,
  ])

  const handleUpdateActionSelection = useCallback(
    (actionType: UpdateActionType): void => {
      if (institutionId === undefined) return

      switch (actionType) {
        case UpdateActionType.RELINK_INSTITUTION:
          void initiateRelinkPlaidExternalInstitution({ institutionId })
          break
        case UpdateActionType.UPDATING_INSTITUTION:
          void initiateUpdatePlaidExternalInstitution({ institutionId })
          break
        case UpdateActionType.REMOVE_INSTITUTION:
          void unlinkPlaidExternalInstitution({ institutionId })
          break
      }
    },
    [
      institutionId,
      initiateRelinkPlaidExternalInstitution,
      initiateUpdatePlaidExternalInstitution,
      unlinkPlaidExternalInstitution,
    ],
  )

  // Plaid SDK handlers
  const handlePlaidSuccess = useCallback(
    (metaData: PlaidLinkOnSuccessMetadata) => {
      // Handle different Plaid success scenarios based on current state
      switch (currentState) {
        case PlaidMultiLinkState.INITIAL_INSTITUTION_LINKING_IN_PROGRESS:
          // New institution link - refetch external accounts and set success state
          void refetchExternalAccounts()
          setCurrentSuccessType(PlaidSuccessType.INITIAL_INSTITUTION_LINK)
          setCurrentState(PlaidMultiLinkState.SUCCESS)
          break

        case PlaidMultiLinkState.RELINKING_INSTITUTION_IN_PROGRESS:
          if (institutionId) {
            void completeRelinkPlaidExternalInstitution({
              institutionId,
              linkToken: plaidLinkToken ?? '',
            })
          }
          break

        case PlaidMultiLinkState.UPDATING_INSTITUTION_IN_PROGRESS:
          // Complete update operation by sending metadata to backend
          if (institutionId) {
            void completeUpdatePlaidExternalInstitution({
              institutionId,
              linkToken: plaidLinkToken ?? '',
              accounts: metaData.accounts.map((account) => ({
                accountId: account.id,
                name: account.name,
                mask: account.mask,
                type: account.type,
                subtype: account.subtype,
              })),
            })
          }
          break

        default:
          // Other states don't require handling in success callback
          break
      }
    },
    [
      currentState,
      institutionId,
      plaidLinkToken,
      refetchExternalAccounts,
      completeRelinkPlaidExternalInstitution,
      completeUpdatePlaidExternalInstitution,
    ],
  )

  const handlePlaidError = useCallback(() => {
    // Determine error type based on current state
    let errorType: PlaidErrorType
    switch (currentState) {
      case PlaidMultiLinkState.INITIAL_INSTITUTION_LINKING_IN_PROGRESS:
        errorType = PlaidErrorType.INITIAL_INSTITUTION_LINK_ERROR
        break
      case PlaidMultiLinkState.RELINKING_INSTITUTION_IN_PROGRESS:
        errorType = PlaidErrorType.RELINK_INSTITUTION_ERROR
        break
      case PlaidMultiLinkState.UPDATING_INSTITUTION_IN_PROGRESS:
        errorType = PlaidErrorType.UPDATING_INSTITUTION_ERROR
        break
      default:
        errorType = PlaidErrorType.INITIAL_INSTITUTION_LINK_ERROR
        break
    }

    setCurrentErrorType(errorType)
    setCurrentState(PlaidMultiLinkState.ERROR)
    sendEvent('eg_plaidMultiLink_linkingError', {
      eventSource: 'egWealth',
      category: 'plaid-multi-link',
      subCategory: 'plaid-sdk',
      linkType: selectedLinkType,
      institutionId,
      errorType,
    })
  }, [currentState, selectedLinkType, institutionId])

  const handlePlaidExit = useCallback(() => {
    // Resets state and closes modal
    closeModal()
  }, [closeModal])

  return {
    // Modal state
    isOpen,
    institutionId,
    modalTitle,
    isChaseBank,

    // Plaid flow state
    currentState,
    selectedLinkType,
    plaidLinkToken,
    INITIAL_STATE,

    // Error and success state
    currentErrorType,
    currentSuccessType,

    // Loading states
    isCreatingPlaidMultiLinkToken,
    isUnlinkingPlaidExternalInstitution,
    isInitiatingRelinkPlaidExternalInstitution,
    isCompletingRelinkPlaidExternalAccount,
    isUpdatingPlaidExternalInstitution,
    isCompletingUpdatePlaidExternalAccount,
    // Show Relink Button if account is expired or expiring soon
    showRelink,

    // Modal control methods
    openModal,
    closeModal,

    // Plaid flow handlers
    handleAccountTypeSelection,
    handleUpdateActionSelection,
    handleTryAgain,
    handlePlaidSuccess,
    handlePlaidError,
    handlePlaidExit,
  }
}

// Create context provider using the hook
const [usePlaidMultiLinkContext, PlaidMultiLinkContextProvider] = createContext(usePlaidMultiLink)

export { PlaidMultiLinkContextProvider, usePlaidMultiLinkContext }
